/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles,
  Switch,
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Eye as EyeIcon,
  Search as SearchIcon,
  Star as StarIcon,
} from 'react-feather';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import BlogRequest from '../../../requests/api/blog';
import SuccessStoryRequest from '../../../requests/api/successStory';
import { BLOG } from '../../../utils/const/contentTypes';
import { LANDING_URL } from '../../../config';
import sortOptions, { DEFAULT_SORT } from './sortOptions';

// Cada filtro ocupa 2 de las 12 columnas en desktop; la busqueda toma el resto
const FILTER_COLUMN_WIDTH = 2;
const BLOG_FILTER_COUNT = 4;
const SUCCESS_STORY_FILTER_COUNT = 3;

const useStyles = makeStyles((theme) => ({
  root: {},
  bulkOperations: {
    position: 'relative',
  },
  bulkActions: {
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: 6,
    position: 'absolute',
    width: '100%',
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
  },
  bulkAction: {
    marginLeft: theme.spacing(2),
  },
  thumbnail: {
    height: 42,
    width: 42,
    marginRight: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  starFilled: {
    color: theme.palette.warning?.main || '#f0ad4e',
  },
  starEmpty: {
    color: theme.palette.action.disabled,
  },
}));

const editPathFor = (contentType, id) => (
  contentType === BLOG
    ? `/administracion/blog/${id}/editar`
    : `/administracion/casos-exito/${id}/editar`
);

const viewUrlFor = (contentType, slug) => (
  contentType === BLOG
    ? `${LANDING_URL}/blog/${slug}`
    : `${LANDING_URL}/casos-de-exito/${slug}`
);

const imageFor = (contentType, row) => (
  contentType === BLOG ? row.thumbnail : row.coverImage
);

const searchPlaceholderFor = (contentType) => (
  contentType === BLOG
    ? 'Buscar blog por título o tags'
    : 'Buscar caso de éxito por título'
);

const publishItems = (contentType, items) => (
  contentType === BLOG
    ? BlogRequest.postBlogPublish({ blogs: items })
    : SuccessStoryRequest.postSuccessStoryPublish({ successStories: items })
);

function Results({
  className,
  contentType,
  rows,
  pageOfTable,
  setPageOfTable,
  limit,
  setLimit,
  searchValue,
  setSearchValue,
  count,
  clusters,
  clusterId,
  setClusterId,
  publishedFilter,
  setPublishedFilter,
  featuredFilter,
  setFeaturedFilter,
  sort,
  setSort,
  ...rest
}) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentRows, setCurrentRows] = useState(rows);

  useEffect(() => { setCurrentRows(rows); setSelectedRows([]); }, [rows]);

  const filterCount = contentType === BLOG ? BLOG_FILTER_COUNT : SUCCESS_STORY_FILTER_COUNT;
  const searchColumnWidth = 12 - (filterCount * FILTER_COLUMN_WIDTH);

  const handleQueryChange = (event) => {
    event.persist();
    setSearchValue(event.target.value);
  };

  const handleSortChange = (event) => {
    event.persist();
    setSort(event.target.value);
    setPageOfTable(0);
  };

  const handleClusterFilterChange = (event) => {
    event.persist();
    setClusterId(event.target.value);
    setPageOfTable(0);
  };

  const handlePublishedFilterChange = (event) => {
    event.persist();
    setPublishedFilter(event.target.value);
    setPageOfTable(0);
  };

  const handleFeaturedFilterChange = (event) => {
    event.persist();
    setFeaturedFilter(event.target.value);
    setPageOfTable(0);
  };

  const handleSelectAllRows = (event) => {
    setSelectedRows(event.target.checked
      ? currentRows.map((row) => row.id)
      : []);
  };

  const handleSelectOneRow = (event, rowId) => {
    if (!selectedRows.includes(rowId)) {
      setSelectedRows((prevSelected) => [...prevSelected, rowId]);
    } else {
      setSelectedRows((prevSelected) => prevSelected.filter((id) => id !== rowId));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPageOfTable(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
  };

  const handlePublishedChange = async (itemId) => {
    const item = currentRows.find((r) => r.id === itemId) || {};
    const nextPublished = !item.published;
    const resp = await publishItems(contentType, [{ id: item.id, published: nextPublished }]);
    if (resp.success) {
      setCurrentRows(currentRows.map((r) => (
        r.id === itemId ? { ...r, published: nextPublished } : r
      )));
    } else {
      enqueueSnackbar('Ha ocurrido un error', {
        variant: 'error',
      });
    }
  };

  // Usually query is done on backend with indexing solutions
  const enableBulkOperations = selectedRows.length > 0;
  const selectedSomeRows = selectedRows.length > 0 && selectedRows.length < currentRows.length;
  const selectedAllRows = selectedRows.length === currentRows.length;

  const publishSelected = async (published) => {
    const items = selectedRows.map((sr) => ({ id: sr, published }));
    const resp = await publishItems(contentType, items);
    if (resp.success) {
      enqueueSnackbar(published ? 'Contenido publicado!' : 'Contenido despublicado!', {
        variant: 'success',
      });
      const selectedIds = new Set(selectedRows);
      setCurrentRows(currentRows.map((r) => (
        selectedIds.has(r.id) ? { ...r, published } : r
      )));
      setSelectedRows([]);
    } else {
      enqueueSnackbar(published ? 'No se pudo publicar' : 'No se pudo despublicar', {
        variant: 'error',
      });
    }
  };

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Divider />
      <Box p={2}>
        <Grid
          container
          spacing={2}
          alignItems="center"
        >
          <Grid item xs={12} md={searchColumnWidth}>
            <TextField
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon
                      fontSize="small"
                      color="action"
                    >
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
              }}
              onChange={handleQueryChange}
              placeholder={searchPlaceholderFor(contentType)}
              value={searchValue}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={FILTER_COLUMN_WIDTH}>
            <TextField
              fullWidth
              label="Cluster"
              name="clusterId"
              onChange={handleClusterFilterChange}
              select
              value={clusterId}
              variant="outlined"
            >
              <MenuItem value="">Todos los clusters</MenuItem>
              {clusters.map((cluster) => (
                <MenuItem key={cluster.id} value={cluster.id}>
                  {cluster.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={FILTER_COLUMN_WIDTH}>
            <TextField
              fullWidth
              label="Publicado"
              name="publishedFilter"
              onChange={handlePublishedFilterChange}
              select
              value={publishedFilter}
              variant="outlined"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Publicados</MenuItem>
              <MenuItem value="false">No publicados</MenuItem>
            </TextField>
          </Grid>
          {contentType === BLOG && (
            <Grid item xs={12} sm={6} md={FILTER_COLUMN_WIDTH}>
              <TextField
                fullWidth
                label="Destacado"
                name="featuredFilter"
                onChange={handleFeaturedFilterChange}
                select
                value={featuredFilter}
                variant="outlined"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Destacados</MenuItem>
                <MenuItem value="false">No destacados</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={FILTER_COLUMN_WIDTH}>
            <TextField
              fullWidth
              label="Orden"
              name="sort"
              onChange={handleSortChange}
              select
              value={sort}
              variant="outlined"
            >
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>
      {enableBulkOperations && (
        <div className={classes.bulkOperations}>
          <div className={classes.bulkActions}>
            <Checkbox
              checked={selectedAllRows}
              indeterminate={selectedSomeRows}
              onChange={handleSelectAllRows}
            />
            <Button
              variant="outlined"
              className={classes.bulkAction}
              onClick={() => publishSelected(true)}
            >
              Publicar
            </Button>
            <Button
              variant="outlined"
              className={classes.bulkAction}
              onClick={() => publishSelected(false)}
            >
              Despublicar
            </Button>
          </div>
        </div>
      )}
      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedAllRows}
                    indeterminate={selectedSomeRows}
                    onChange={handleSelectAllRows}
                  />
                </TableCell>
                <TableCell>
                  Título
                </TableCell>
                <TableCell align="center">
                  Ver
                </TableCell>
                <TableCell>
                  Cluster
                </TableCell>
                {contentType === BLOG && (
                  <TableCell align="center">
                    Destacado
                  </TableCell>
                )}
                <TableCell>
                  Autor
                </TableCell>
                <TableCell>
                  Publicado
                </TableCell>
                <TableCell>
                  Fecha publicación
                </TableCell>
                <TableCell align="right">
                  Acción
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row) => {
                const isRowSelected = selectedRows.includes(row.id);
                return (
                  <TableRow
                    hover
                    key={row.id}
                    selected={isRowSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isRowSelected}
                        onChange={(event) => handleSelectOneRow(event, row.id)}
                        value={isRowSelected}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="center"
                      >
                        <Avatar
                          className={classes.thumbnail}
                          src={imageFor(contentType, row)}
                          variant="rounded"
                        />
                        <Typography
                          color="inherit"
                          variant="h6"
                        >
                          {row.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {row.slug ? (
                        <IconButton
                          component="a"
                          href={viewUrlFor(contentType, row.slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <SvgIcon fontSize="small">
                            <EyeIcon />
                          </SvgIcon>
                        </IconButton>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.cluster?.name || '—'}
                    </TableCell>
                    {contentType === BLOG && (
                      <TableCell align="center">
                        <SvgIcon
                          fontSize="small"
                          className={row.featured ? classes.starFilled : classes.starEmpty}
                        >
                          <StarIcon fill={row.featured ? 'currentColor' : 'none'} />
                        </SvgIcon>
                      </TableCell>
                    )}
                    <TableCell>
                      {row.user ? `${row.user.name} ${row.user.lastname}` : '—'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.published}
                        onChange={() => handlePublishedChange(row.id)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.publishedDate || row.createdAt
                        ? moment(row.publishedDate || row.createdAt).format('DD/MM/YYYY HH:mm:ss')
                        : 'Sin publicar'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        component={RouterLink}
                        to={editPathFor(contentType, row.id)}
                      >
                        <SvgIcon fontSize="small">
                          <EditIcon />
                        </SvgIcon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={count}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={pageOfTable}
        rowsPerPage={limit}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Filas por página"
      />
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  contentType: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    slug: PropTypes.string,
    thumbnail: PropTypes.string,
    coverImage: PropTypes.string,
    featured: PropTypes.bool,
    published: PropTypes.bool,
    publishedDate: PropTypes.string,
    createdAt: PropTypes.string,
    cluster: PropTypes.shape({ name: PropTypes.string }),
    user: PropTypes.shape({ name: PropTypes.string, lastname: PropTypes.string }),
  })),
  pageOfTable: PropTypes.number,
  setPageOfTable: PropTypes.func,
  limit: PropTypes.number,
  setLimit: PropTypes.func,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  count: PropTypes.number,
  clusters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  })),
  clusterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setClusterId: PropTypes.func,
  publishedFilter: PropTypes.string,
  setPublishedFilter: PropTypes.func,
  featuredFilter: PropTypes.string,
  setFeaturedFilter: PropTypes.func,
  sort: PropTypes.string,
  setSort: PropTypes.func,
};

Results.defaultProps = {
  rows: [],
  clusters: [],
  clusterId: '',
  setClusterId: () => {},
  publishedFilter: '',
  setPublishedFilter: () => {},
  featuredFilter: '',
  setFeaturedFilter: () => {},
  sort: DEFAULT_SORT,
  setSort: () => {},
};

export default Results;
